import { Circle, ClipboardList, Code, Palette, Zap, Check, Bookmark } from "lucide-react";
import { LogoSmall } from "./Logo";

export function DashboardPreview() {
  return (
    <div className="relative">
      {/* Main Browser Window */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl overflow-hidden border border-border">
        {/* Browser Chrome */}
        <div className="bg-bg-alt px-3 sm:px-4 py-2.5 sm:py-3.5 flex items-center gap-2 sm:gap-2.5 border-b border-border">
          <div className="flex gap-1.5 sm:gap-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#FF5F56]" />
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#FFBD2E]" />
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#27C93F]" />
          </div>
          <div className="flex-1 bg-white border border-border rounded-md sm:rounded-lg px-2.5 sm:px-3.5 py-1.5 sm:py-2 text-[10px] sm:text-xs text-text-muted ml-2 sm:ml-3 truncate">
            specledger.io/dashboard
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="flex flex-col">
          {/* Dashboard Nav */}
          <div className="flex items-center justify-between px-3 sm:px-5 py-2.5 sm:py-3 border-b border-border bg-white">
            <div className="flex items-center gap-2 sm:gap-2.5">
              <LogoSmall className="w-5 h-5 sm:w-7 sm:h-7" />
              <span className="font-semibold text-xs sm:text-[15px] text-text">
                Specledger
              </span>
            </div>
            <span className="text-[10px] sm:text-[13px] text-text-muted flex items-center gap-1 sm:gap-1.5">
              <span className="hidden xs:inline relative">
                <Circle className="w-4 h-4" />
                <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold">1</span>
              </span> Getting Started
            </span>
          </div>

          {/* Dashboard Body */}
          <div className="flex min-h-[220px] sm:min-h-[280px] md:min-h-[320px]">
            {/* Sidebar - Hidden on mobile and small tablets */}
            <div className="hidden md:block w-[180px] lg:w-[220px] xl:w-[260px] bg-[#FAFBFC] border-r border-border p-3 lg:p-4">
              {/* Project Card */}
              <div className="flex items-center gap-2 lg:gap-2.5 p-2 lg:p-2.5 bg-white border border-border rounded-lg mb-3 lg:mb-4">
                <div className="w-7 h-7 lg:w-8 lg:h-8 bg-primary/10 rounded-md lg:rounded-lg flex items-center justify-center text-primary">
                  <ClipboardList className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] lg:text-[13px] font-semibold text-text truncate">
                    Document Management
                  </div>
                  <div className="text-[10px] lg:text-[11px] text-text-muted truncate">
                    Ne4nf/Documatch
                  </div>
                </div>
              </div>

              {/* Phase */}
              <div className="flex items-center gap-1.5 lg:gap-2 mb-2 lg:mb-3">
                <span className="text-[10px] lg:text-[11px] text-text-muted">Phase:</span>
                <span className="bg-[#EEF2FF] text-[#4F46E5] text-[10px] lg:text-[11px] font-semibold px-2 lg:px-2.5 py-0.5 lg:py-1 rounded-md">
                  Requirements
                </span>
              </div>

              {/* Progress Bar */}
              <div className="h-1 bg-border rounded overflow-hidden mb-4 lg:mb-5">
                <div className="h-full w-[35%] bg-primary rounded" />
              </div>

              <div className="text-[10px] lg:text-xs font-semibold text-text mb-2 lg:mb-3">
                Start a conversation
              </div>
              <div className="text-[10px] lg:text-xs text-text-muted leading-relaxed">
                Describe what you&apos;d like to build and I&apos;ll help you
                through requirements, design, and implementation.
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 p-4 sm:p-6 md:p-8 flex flex-col items-center justify-center bg-white">
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-[72px] lg:h-[72px] bg-gradient-to-br from-primary to-accent rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4 md:mb-5 shadow-[0_8px_24px_rgba(124,58,237,0.25)]">
                <Code className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
              </div>

              <p className="text-base sm:text-lg md:text-xl lg:text-[22px] font-bold text-text mb-1.5 sm:mb-2 text-center">
                Welcome to Specledger
              </p>
              <p className="text-xs sm:text-sm text-text-muted text-center max-w-[280px] sm:max-w-[320px] md:max-w-[340px] leading-relaxed mb-4 sm:mb-5 md:mb-7">
                Your AI development partner for spec-driven development.
                I&apos;ll help you go from requirements to design to
                implementation.
              </p>

              {/* Pillars */}
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3 md:gap-4">
                <div className="p-2.5 sm:p-3 md:p-4 border border-border rounded-lg sm:rounded-xl text-center min-w-[90px] sm:min-w-[110px] md:min-w-[120px] lg:min-w-[140px] transition-all hover:border-primary hover:bg-primary/5">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 mx-auto mb-1.5 sm:mb-2 md:mb-2.5 flex items-center justify-center text-[#3B82F6]">
                    <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                  </div>
                  <div className="text-[11px] sm:text-xs md:text-[13px] font-semibold text-text mb-0.5 sm:mb-1">
                    Requirements
                  </div>
                  <div className="text-[9px] sm:text-[10px] md:text-[11px] text-text-muted">
                    Define user needs
                  </div>
                </div>

                <div className="p-2.5 sm:p-3 md:p-4 border border-border rounded-lg sm:rounded-xl text-center min-w-[90px] sm:min-w-[110px] md:min-w-[120px] lg:min-w-[140px] transition-all hover:border-primary hover:bg-primary/5">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 mx-auto mb-1.5 sm:mb-2 md:mb-2.5 flex items-center justify-center text-primary-light">
                    <Palette className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                  </div>
                  <div className="text-[11px] sm:text-xs md:text-[13px] font-semibold text-text mb-0.5 sm:mb-1">
                    Design
                  </div>
                  <div className="text-[9px] sm:text-[10px] md:text-[11px] text-text-muted">
                    Plan architecture
                  </div>
                </div>

                <div className="p-2.5 sm:p-3 md:p-4 border border-border rounded-lg sm:rounded-xl text-center min-w-[90px] sm:min-w-[110px] md:min-w-[120px] lg:min-w-[140px] transition-all hover:border-primary hover:bg-primary/5">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 mx-auto mb-1.5 sm:mb-2 md:mb-2.5 flex items-center justify-center text-success">
                    <Zap className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                  </div>
                  <div className="text-[11px] sm:text-xs md:text-[13px] font-semibold text-text mb-0.5 sm:mb-1">
                    Implementation
                  </div>
                  <div className="text-[9px] sm:text-[10px] md:text-[11px] text-text-muted">Generate code</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Elements - Hidden on mobile/tablet */}
      <div className="hidden xl:flex absolute -top-5 -right-10 bg-white rounded-xl px-4 py-3 shadow-xl items-center gap-2.5 animate-float z-10">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
          <Check className="w-5 h-5" />
        </div>
        <div>
          <div className="font-semibold text-text text-xs">Spec Approved</div>
          <div className="text-text-muted text-[11px]">Auth Flow v2.1</div>
        </div>
      </div>

      <div className="hidden xl:flex absolute bottom-10 -left-8 bg-white rounded-xl px-4 py-3.5 shadow-xl items-center gap-2.5 animate-float-delayed z-10">
        <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center text-success">
          <Bookmark className="w-5 h-5" />
        </div>
        <div>
          <div className="font-semibold text-text text-xs">
            Checkpoint Created
          </div>
          <div className="text-text-muted text-[11px]">Payment Integration</div>
        </div>
      </div>
    </div>
  );
}
